import { CopyButton } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { CROSSTAB_GENERATOR_STRINGS } from "@/components/tools/crosstab-generator";
import { ResultTable } from "@/components/tools/stats-descriptive/ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ApaTableModel,
  apaTableHtml,
  apaTableText,
  buildApaTable,
  cellRuns,
} from "@/lib/tool-kit/paper-formatting/apa-table";
import {
  machineNumber,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  toCsv,
  toMarkdownTable,
} from "@/lib/tool-kit/stats-descriptive/frequency";
import {
  apaCrosstabRows,
  type Crosstab,
  countRows,
  type PercentKind,
  percentRows,
} from "@/lib/tool-kit/stats-descriptive-more/crosstab";

type Strings = typeof CROSSTAB_GENERATOR_STRINGS;

export interface CrosstabView {
  strings: Strings;
  fmt: NumberFormatter;
}

export const PERCENT_KINDS: readonly PercentKind[] = ["row", "column", "total"];

function percentFormatter(view: CrosstabView) {
  return (share: number) =>
    view.fmt(share, {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
}

function kindTitle(kind: PercentKind, strings: Strings): string {
  if (kind === "row") {
    return strings.titleRowPercent;
  }
  return kind === "column"
    ? strings.titleColumnPercent
    : strings.titleTotalPercent;
}

function kindNote(kind: PercentKind, strings: Strings): string {
  if (kind === "row") {
    return strings.noteRow;
  }
  return kind === "column" ? strings.noteColumn : strings.noteTotal;
}

/** The APA model for the chosen percentage kind. */
export function apaModel(
  ct: Crosstab,
  kind: PercentKind,
  names: { row: string; col: string; number: string },
  view: CrosstabView
): ApaTableModel {
  const { strings, fmt } = view;
  const rows = apaCrosstabRows(
    ct,
    kind,
    { stub: names.row, total: strings.total },
    (n) => fmt(n),
    percentFormatter(view)
  );
  return buildApaTable(rows, {
    tableWord: strings.apaTable,
    noteWord: strings.apaNote,
    number: names.number,
    title: t(strings.apaTitle, { row: names.row, col: names.col }),
    titleCase: true,
    decimalAlign: false,
    totalRow: true,
    generalNote: kindNote(kind, strings),
    specificNotes: "",
    probabilityNote: false,
  });
}

function ApaPreview({ model }: { model: ApaTableModel }) {
  const cell = "px-3 py-1.5 text-center align-top";
  return (
    <div className="space-y-1 overflow-x-auto rounded-lg border border-border bg-card p-4 text-foreground text-sm">
      <p className="font-bold">{model.label}</p>
      <p className="italic">{model.title}</p>
      <table className="mt-2 border-collapse border-foreground border-y">
        <thead>
          <tr className="border-foreground border-b">
            {model.head.map((heading, index) => (
              <th
                className={`${cell} font-normal ${index === 0 ? "text-left" : ""}`}
                key={heading}
                scope="col"
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.body.map((row, rowIndex) => (
            <tr
              className={
                rowIndex === model.body.length - 1 && model.totalRow
                  ? "border-foreground border-t"
                  : ""
              }
              key={row[0]}
            >
              {row.map((value, index) => (
                <td
                  className={`${cell} whitespace-nowrap ${index === 0 ? "text-left" : ""}`}
                  // biome-ignore lint/suspicious/noArrayIndexKey: positional cells
                  key={index}
                >
                  {value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pt-1 text-xs">
        <em>{model.noteWord}.</em>{" "}
        {cellRuns(model.generalNote).map((run, index) =>
          run.style === "em" ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional runs
            <em key={index}>{run.text}</em>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: positional runs
            <span key={index}>{run.text}</span>
          )
        )}
      </p>
    </div>
  );
}

/** Counts, the three percentage tables, the APA table and the copy buttons. */
export function CrosstabTables({
  ct,
  kind,
  names,
  view,
}: {
  ct: Crosstab;
  kind: PercentKind;
  names: { row: string; col: string; number: string };
  view: CrosstabView;
}) {
  const { strings, fmt } = view;
  const head = [names.row, ...ct.colLabels, strings.total];
  const percent = percentFormatter(view);
  const counts = countRows(ct, strings.total, (n) => fmt(n));
  const model = apaModel(ct, kind, names, view);
  const csvRows = percentRows(ct, kind, strings.total, (x) =>
    machineNumber(x * 100, 4)
  );
  return (
    <>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.titleCounts}</h3>
        <ResultTable head={head} rows={counts} wrapFirstColumn />
      </div>
      {PERCENT_KINDS.map((each) => (
        <div className="space-y-2" key={each}>
          <h3 className={WIDGET_H3}>{kindTitle(each, strings)}</h3>
          <ResultTable
            head={head}
            rows={percentRows(ct, each, strings.total, percent)}
            wrapFirstColumn
          />
        </div>
      ))}
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.apaHeading}</h3>
        <ApaPreview model={model} />
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            html={apaTableHtml(model)}
            label={strings.copyApa}
            text={apaTableText(model)}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyCsv}
            text={toCsv(head, countRows(ct, strings.total, String))}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyMarkdown}
            text={toMarkdownTable(head, countRows(ct, strings.total, String))}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyPercentCsv}
            text={toCsv(head, csvRows)}
          />
        </div>
      </div>
    </>
  );
}
